import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { OrganizationData, OrganizationStore } from '@telespot/management/ui';
import { distinctUntilChanged, filter, map, skip, startWith, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { AuthService } from '@telespot/web-core';

export interface NavBarOrganizationState {
  currentOrganizationLogo?: string;
  loadingLogo: boolean;
  visible: boolean;
}

@Injectable()
export class NavBarOrganizationFacade extends ComponentStore<NavBarOrganizationState> {
  constructor(private readonly organizationStore: OrganizationStore, private readonly authService: AuthService) {
    super({ visible: false, currentOrganizationLogo: undefined, loadingLogo: true });

    // Set current organization logo from the organization store
    this.onStoreOrganizationDefined(this.organizationStore.currentOrganization$);

    // Set filter to only show organizations for current user
    this.onUserChange(this.authService.currentUser$.pipe(map((user) => user?.id)));

    // Reload the (paginated) org list when the user's set of organizations
    // changes — e.g. right after creating one — so the selector reflects it
    // without a page refresh. The org editor runs on a different store instance,
    // so we react to the shared AuthService signal rather than the store.
    this.reloadOnOrganizationsChange(this.authService.organizations$);
  }

  private readonly reloadOnOrganizationsChange = this.effect((organizations$: Observable<{ id: string }[]>) =>
    organizations$.pipe(
      map((organizations) =>
        (organizations ?? [])
          .map((organization) => organization.id)
          .sort()
          .join(',')
      ),

      // A logged-in user always has at least one org, so an empty list never needs a paginated fetch.
      filter((ids) => ids.length > 0),
      distinctUntilChanged(),
      // The initial list load is handled by onUserChange/filterByUser; only react
      // to subsequent changes (an org added).
      skip(1),
      tap(() => this.organizationStore.initializeOrganizations())
    )
  );

  public readonly currentOrganization$ = this.organizationStore.currentOrganization$;
  public readonly organizations$ = this.organizationStore.organizations$;
  public readonly loading$ = this.organizationStore.loading$;
  public readonly searchTerm$ = this.organizationStore.searchTerm$;
  public readonly previousTokens$ = this.organizationStore.previousTokens$;
  public readonly nextToken$ = this.organizationStore.nextToken$;
  public readonly totalCount$ = this.organizationStore.totalCount$;
  public readonly pageSize$ = this.organizationStore.pageSize$;
  public readonly hasMoreItems$ = this.organizationStore.hasMoreItems$;

  public readonly loadingLogo$ = this.select((state) => state.loadingLogo);
  public readonly visible$ = this.select((state) => state.visible);

  public readonly hideArrows$ = combineLatest([
    this.organizationStore.totalCount$,
    this.organizationStore.pageSize$,
  ]).pipe(
    map(([totalCount, pageSize]) => totalCount <= pageSize),
    startWith(true)
  );

  public readonly currentOrganizationLogo$ = this.select((state) => state.currentOrganizationLogo);

  private readonly setCurrentOrganizationLogo = this.updater((state, currentOrganizationLogo: string | undefined) => ({
    ...state,
    currentOrganizationLogo,
    loadingLogo: false,
  }));

  private readonly onStoreOrganizationDefined = this.effect(
    (organization$: Observable<OrganizationData | undefined>) => {
      return organization$.pipe(
        tap((organization: OrganizationData | undefined) => {
          if (organization) {
            // Always update — the updater also flips loadingLogo to false.
            // The previous "skip if same URL" check left loadingLogo stuck
            // at true when the org had no logo (undefined !== undefined).
            this.setCurrentOrganizationLogo(organization.logoUrl);
          }
        })
      );
    }
  );

  private readonly onUserChange = this.effect((userId$: Observable<string | undefined>) => {
    return userId$.pipe(
      tap((userId: string | undefined) => {
        if (userId) {
          this.organizationStore.setFilterByUser(userId);
          this.patchState({ visible: true });
        } else {
          this.patchState({ visible: false });
        }
      })
    );
  });

  public readonly selectOrganization = this.effect((organizationId$: Observable<string>) => {
    return organizationId$.pipe(
      withLatestFrom(this.organizationStore.currentOrganizationId$),
      tap(([organizationId, currentOrganizationId]: [string, string | undefined]) => {
        if (currentOrganizationId !== organizationId) {
          this.patchState({ loadingLogo: true });
          this.authService.setOrganizationFromId(organizationId);

          // The store organization ID will be updated after detecting a change in the auth service
          // See the onAuthOrganizationChange effect in the organization store for more details
          // This behaviour will need to change when we get rid of the auth service
          // For now, we'll leave the next line commented out, but in the future we may need to uncomment it
          // this.organizationStore.setCurrentOrganizationId(organizationId);
        }
      })
    );
  });

  public searchTermChanged(searchTerm: string): void {
    this.organizationStore.setSearchTerm(searchTerm);
  }

  public loadMoreOrganizations(): void {
    this.organizationStore.loadMoreOrganizations(undefined);
  }
}
